import React from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import {
  useIsSellerOnNonWalletHighVolumeUsageBasedBillingPlan, // only has one consumer outside of this file
} from './non-wallet-access-hooks';
import {
  isBillingPlanIdUsageBilledHighVolume,
  HighVolumePlanToUsagePriceMap,
  BillingPlansThatIncludeNonWalletedAccess,
} from '../../enums/billing-plan-ids';
import { Features } from '../../constants/features';
import Global from '../../store/global-selectors';
import FormattedCurrency from '../../components/formatted-currency';
import UserDateFormat from '../../components/user-date-format';
import { getDollarValueAsCents } from '../../services/decimal-display-service';
import { messages as yourCarriersMessages } from '../../pages/settings/payment-and-subscription/components/manage-subscription-drawer/your-carriers/messages';
import { useIsSellerFeatureEnabled } from '../use-is-seller-feature-enabled';
import { useIsTrialActive } from '../use-is-trial-active';
import { useNonWalletedAndNonExcludedSellerLabelProviders } from '../use-seller-label-providers';
import {
  getCurrentLineItems,
  findPlanReferenceByCode,
  isFeatureIncludedInBillingPlan,
} from '../../pages/settings/payment-and-subscription/utils/plan-utilities';
import { NumberFormattingService } from 'ui-components/services/number-formatting-service';

/** @typedef {'Included' | 'FreeDuringTrial' | 'Paid'} NonWalletAccessBillingStateValue */

/**
 * How BYOA is being billed for the current seller:
 * - `Included`: permanently free under the seller's current arrangement (alpha grant or bundled into the plan).
 *   Toggle should be disabled.
 * - `FreeDuringTrial`: free right now because the seller has an active BYOA trial line item,
 *   but will be charged after the trial ends. Toggle stays enabled.
 * - `Paid`: seller is being charged for BYOA as a paid add-on.
 */
export const NonWalletAccessBillingState = Object.freeze({
  Included: 'Included',
  FreeDuringTrial: 'FreeDuringTrial',
  Paid: 'Paid',
});

/**
 * @returns {NonWalletAccessBillingStateValue}
 */
export const useNonWalletAccessBillingState = () => {
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);
  const planInfo = useSelector(Global.selectSellerPlanInfo);
  const { planToNonWalletAccessPlanMap = {}, currentBillingPlan } = subscriptionData ?? {};

  const isOnByoaPlan = Object.values(planToNonWalletAccessPlanMap).some(
    (byoaPlan) => byoaPlan.billingPlanId === currentBillingPlan?.billingPlanId,
  );

  const lineItems = getCurrentLineItems(subscriptionData, planInfo);
  const byoaLineItem = lineItems.find((lineItem) => lineItem.featureIds?.includes(Features.NonWalletProviderAccess));
  if (!isOnByoaPlan && (byoaLineItem?.isAlphaUser || byoaLineItem?.isIncludedInPlan)) {
    return NonWalletAccessBillingState.Included;
  }
  if (byoaLineItem?.isTrial) {
    return NonWalletAccessBillingState.FreeDuringTrial;
  }
  return NonWalletAccessBillingState.Paid;
};

/**
 * Returns the appropriate `<FormattedMessage>` element describing how BYOA is billed
 * for this seller, given the selected plan (used for price computation). Shared by the
 * Manage Subscription Drawer's selection toggle and plan-summary.
 * @param {object} args
 * @param {{ billingPlanId?: string | number, pricePerPeriod?: { value?: number, code?: string } }} args.selectedPlan
 * @returns {React.ReactNode}
 */
export const useNonWalletAccessDescription = ({ selectedPlan }) => {
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);
  const planInfo = useSelector(Global.selectSellerPlanInfo);
  const billingCurrencyCode = useSelector(Global.selectSellerSubscriptionCurrency);
  const billingState = useNonWalletAccessBillingState();
  const { planToNonWalletAccessPlanMap = {} } = subscriptionData ?? {};
  const isHighVolumePlanSelected = isBillingPlanIdUsageBilledHighVolume(selectedPlan?.billingPlanId);

  if (isHighVolumePlanSelected) {
    const usageFee = HighVolumePlanToUsagePriceMap[selectedPlan?.billingPlanId] ?? 0;
    return (
      <FormattedMessage {...yourCarriersMessages.yourCarriersDescriptionHighVolume} values={{ fee: getDollarValueAsCents(usageFee) }} />
    );
  }
  if (billingState === NonWalletAccessBillingState.Included) {
    return <FormattedMessage {...yourCarriersMessages.yourCarriersDescriptionIncluded} />;
  }
  const byoaPlan = planToNonWalletAccessPlanMap?.[selectedPlan?.billingPlanId];
  const byoaPriceValue = getNonWalletAccessDisplayPrice(selectedPlan, byoaPlan);
  const byoaCharge = <FormattedCurrency currencyCode={billingCurrencyCode} fractionDigits={2} value={byoaPriceValue} />;
  if (billingState === NonWalletAccessBillingState.FreeDuringTrial) {
    const lineItems = getCurrentLineItems(subscriptionData, planInfo);
    const byoaLineItem = lineItems.find((lineItem) => lineItem.featureIds?.includes(Features.NonWalletProviderAccess));
    const trialEndDate = <UserDateFormat value={byoaLineItem?.trialEndDateTimeUtc} />;
    if (byoaPriceValue === 0) {
      return <FormattedMessage {...yourCarriersMessages.yourCarriersDescriptionDisabledAfterTrial} values={{ trialEndDate }} />;
    }
    return <FormattedMessage {...yourCarriersMessages.yourCarriersDescriptionFreeDuringTrial} values={{ trialEndDate, byoaCharge }} />;
  }
  return <FormattedMessage {...yourCarriersMessages.yourCarriersDescription} values={{ byoaCharge }} />;
};

/**
 * (Presentation only) used in various UI components to show BYOA-related info (or not)
 * @returns {boolean}
 */
export const useIsNonWalletAccessVisible = () => {
  const hasByoaFeature = useIsSellerFeatureEnabled(Features.NonWalletProviderAccess);
  const isInTrial = useIsTrialActive();
  const nonWalletAndNonExcludedSellerLabelProviders = useNonWalletedAndNonExcludedSellerLabelProviders();

  return hasByoaFeature || (isInTrial && nonWalletAndNonExcludedSellerLabelProviders.length > 0);
};

/**
 * (Presentation only) controls pill and toggle state when user is downgrading to a plan that lacks BYOA. Enum value: NON_WALLET_ELIGIBLE_PENDING_INACTIVE
 * @returns {boolean}
 */
export const useIsLosingNonWalletAccess = () => {
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData) ?? {};
  const { currentBillingPlan, currentSubscription } = subscriptionData;
  const { code } = currentBillingPlan ?? {};
  const { pendingPlanCode } = currentSubscription ?? {};
  if (!code || !pendingPlanCode) return false;

  const pendingPlan = findPlanReferenceByCode(pendingPlanCode, subscriptionData);
  const pendingPlanId = parseInt(String(pendingPlan?.billingPlanId), 10);

  const pendingPlanHasByoa =
    BillingPlansThatIncludeNonWalletedAccess.includes(pendingPlanId) ||
    isFeatureIncludedInBillingPlan(pendingPlan, Features.NonWalletProviderAccess);

  return !pendingPlanHasByoa;
};

/**
 * (Presentation only) Is used in the old subscription plan drawer to indicate the loss of the BYOA feature
 * @returns {boolean}
 */
export const useIsTrialWithByoaProviders = () => {
  const isInTrial = useIsTrialActive();
  const nonWalletAndNonExcludedSellerLabelProviders = useNonWalletedAndNonExcludedSellerLabelProviders();

  return isInTrial && nonWalletAndNonExcludedSellerLabelProviders.length > 0;
};

/**
 * (Presentation only) Displays the per-shipment price in the user's subscription overview card
 * @param {number | string} billingPlanId
 * @returns {number | null}
 */
export const useGetPerLabelFeeForNonWalletAccessIncludedPlan = (billingPlanId) => {
  const isOnNonWalletAccessHighVolumeUsageBasedBillingPlan = useIsSellerOnNonWalletHighVolumeUsageBasedBillingPlan();
  if (isOnNonWalletAccessHighVolumeUsageBasedBillingPlan) {
    return HighVolumePlanToUsagePriceMap[billingPlanId] ?? null;
  }

  return null;
};

/**
 * (Presentation only) Used to display a plan price
 * @param {*} basePlan
 * @param {*} nonWalletAccessPlan
 * @returns {number}
 */
export const getNonWalletAccessDisplayPrice = (basePlan, nonWalletAccessPlan) => {
  const billingPlanId = parseInt(basePlan?.billingPlanId ?? 0, 10);
  const nonWalletAccessAddOnPrice = nonWalletAccessPlan?.pricePerPeriod?.value ?? 0;
  const selectedPlanPrice = basePlan?.pricePerPeriod?.value ?? 0;
  const planHasNonWalletAccessEquivalent =
    (nonWalletAccessAddOnPrice && selectedPlanPrice) || isBillingPlanIdUsageBilledHighVolume(billingPlanId);
  const planNonWalletAccessPrice = isBillingPlanIdUsageBilledHighVolume(billingPlanId)
    ? HighVolumePlanToUsagePriceMap[billingPlanId]
    : NumberFormattingService.roundToPlace(nonWalletAccessAddOnPrice - selectedPlanPrice, 2);
  return planHasNonWalletAccessEquivalent ? planNonWalletAccessPrice : 0;
};

/**
 * @param {{ billingPlanId?: string | number }} selectedPlan
 * @returns {boolean}
 */
export const useIsByoaPriceUnknownDuringTrial = (selectedPlan) => {
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);
  const billingState = useNonWalletAccessBillingState();
  const { planToNonWalletAccessPlanMap = {} } = subscriptionData ?? {};
  const byoaPlan = planToNonWalletAccessPlanMap?.[selectedPlan?.billingPlanId];

  if (billingState !== NonWalletAccessBillingState.FreeDuringTrial) {
    return false;
  }
  return getNonWalletAccessDisplayPrice(selectedPlan, byoaPlan) === 0;
};

/**
 * (Presentation only) Sometimes returns an asterisk for a plan price amount
 * @param {*} selectedPlan
 * @returns {string}
 */
export const getNonWalletAccessDisplayPriceNote = (selectedPlan) => {
  const shouldReturnAsterisk = isBillingPlanIdUsageBilledHighVolume(selectedPlan?.billingPlanId);
  return shouldReturnAsterisk ? '*' : '';
};

/**
 * (Presentation Only) Used to display a plan price amount next to its name
 * @param {*} currentBillingPlan
 * @param {*} planToNonWalletAccessPlanMap
 * @param {any[]} allPlans
 * @returns {*}
 */
export const getBasePlanForNonWalletAccessPlan = (currentBillingPlan, planToNonWalletAccessPlanMap, allPlans) => {
  const [walletAccessBillingPlanId] =
    Object.entries(planToNonWalletAccessPlanMap).find(([, { billingPlanId }]) => currentBillingPlan?.billingPlanId === billingPlanId) ?? [];
  return allPlans.find((plan) => plan.billingPlanId === walletAccessBillingPlanId) || currentBillingPlan;
};
